import { validateAction } from "@grundbesitz/engine";
import type { EngineAction, PlayerAction, PlayerId, StateView } from "@grundbesitz/shared";

/** Keys are compared as `KeyboardEvent.key` in lower case, " " is the space bar. */
interface ActionHotkey {
  keys: string[];
  action: PlayerAction;
  label: string;
}

/**
 * Game actions on the keyboard. A key may stand for several actions as long as only one of them can be
 * allowed at a time (Space rolls before the move and ends the turn after it, K buys or draws a card).
 */
export const ACTION_HOTKEYS: ActionHotkey[] = [
  { keys: [" ", "w"], action: { type: "ROLL_DICE" }, label: "Würfeln (im Gefängnis: Pasch versuchen)" },
  { keys: [" ", "e"], action: { type: "END_TURN" }, label: "Zug beenden" },
  { keys: ["k"], action: { type: "BUY_PROPERTY" }, label: "Grundstück kaufen" },
  { keys: ["v"], action: { type: "DECLINE_PURCHASE" }, label: "Nicht kaufen, versteigern" },
  { keys: ["b"], action: { type: "PAY_JAIL_FINE" }, label: "Kaution bezahlen" },
  { keys: ["f"], action: { type: "USE_JAIL_CARD" }, label: "Freikarte einsetzen" },
  { keys: ["z"], action: { type: "CHOOSE_CARD_OPTION", choice: "PAY" }, label: "Strafe zahlen" },
  { keys: ["k"], action: { type: "CHOOSE_CARD_OPTION", choice: "DRAW" }, label: "Ereigniskarte ziehen" },
  { keys: ["m"], action: { type: "CLAIM_RENT" }, label: "Miete fordern" },
];

/** Number keys open the panels, in the order of the tabs. */
export const TAB_HOTKEYS = [
  { key: "1", tab: "players", label: "Spieler" },
  { key: "2", tab: "assets", label: "Besitz" },
  { key: "3", tab: "trade", label: "Handel" },
  { key: "4", tab: "chat", label: "Chat & Log" },
] as const;

export const HELP_KEY = "?";

/** The action a key triggers right now, null when it does nothing (wrong phase, not your move). */
export function hotkeyAction(s: StateView, pid: PlayerId | null, key: string): PlayerAction | null {
  if (!pid) return null;
  for (const h of ACTION_HOTKEYS) {
    if (!h.keys.includes(key)) continue;
    if (validateAction(s, { ...h.action, player: pid } as EngineAction) === null) return h.action;
  }
  return null;
}

const sameAction = (a: PlayerAction, b: PlayerAction) => JSON.stringify(a) === JSON.stringify(b);

/** Keys of an action button, empty when it has none. */
export function keysFor(action: PlayerAction): string[] {
  return ACTION_HOTKEYS.find((h) => sameAction(h.action, action))?.keys ?? [];
}

/** "Leertaste", "W", "?" */
export function keyLabel(key: string): string {
  return key === " " ? "Leertaste" : key.toUpperCase();
}

/** Value for `aria-keyshortcuts`: "Space W". */
export function ariaKeys(keys: string[]): string | undefined {
  return keys.length > 0 ? keys.map((k) => (k === " " ? "Space" : k.toUpperCase())).join(" ") : undefined;
}

/** Typing in a form field must never trigger a hotkey. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as Partial<HTMLElement> | null;
  if (!el || typeof el.tagName !== "string") return false;
  return el.isContentEditable === true || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}
